import { useCallback, useEffect, useRef, useState } from 'react'
import { type Location, useBlocker } from 'react-router-dom'
import { observer } from 'mobx-react'

import { OrderRoutePathsEnum } from 'Modules/order/routes/orderRouteConfig.types'
import { RentalsRoutePathsEnum } from 'Modules/rentals/routes/rentalsRouteConfig.types'

import { PreviewColumnTypeEnum } from 'Components/PreviewColumn/PreviewColumn.types'

import { useStore } from 'Stores/index'

import { useModal } from 'Hooks'

import { OrderGuardModalContent } from './OrderGuardModalContent'

export const OrderGuardModal = observer(() => {
  const [isNavigationAllowed, setIsNavigationAllowed] = useState(false)
  const { orderStore, portalSettingsStore } = useStore()
  const { setChosenDevice, setFetchError, setMutationError, mutationError } =
    orderStore
  const { setTogglePreviewColumn, previewColumnSettings } = portalSettingsStore

  const { setModal, hideModal } = useModal()

  const handleLeaveOrder = useCallback(() => {
    setIsNavigationAllowed(true)
    setChosenDevice(null)
    setFetchError(false)
    setMutationError(false)

    const existingPreviewColumnBundleSetting = previewColumnSettings.find(
      previewColumnSetting =>
        previewColumnSetting.type === PreviewColumnTypeEnum.Bundle,
    )

    if (
      existingPreviewColumnBundleSetting &&
      existingPreviewColumnBundleSetting.isOpen
    ) {
      setTogglePreviewColumn(PreviewColumnTypeEnum.Bundle, false)
    }
  }, [
    previewColumnSettings,
    setChosenDevice,
    setFetchError,
    setMutationError,
    setTogglePreviewColumn,
  ])

  const blocker = useBlocker(
    ({
      currentLocation,
      nextLocation,
    }: {
      currentLocation: Location
      nextLocation: Location
    }) => {
      // Disable GuardModal when redirecting user between prolonging and upgrade
      const disableRouteGuard =
        (currentLocation.pathname.includes('/order/prolong/') &&
          nextLocation.pathname.includes('/order/upgrade')) ||
        (currentLocation.pathname.includes('/order/upgrade') &&
          nextLocation.pathname.includes('/order/prolong'))

      const isNavigationEnabled =
        disableRouteGuard ||
        nextLocation.pathname.includes(
          OrderRoutePathsEnum.ORDER_CONFIRMATION,
        ) ||
        nextLocation.pathname.includes(
          OrderRoutePathsEnum.ORDER_CUSTOM_CONFIRMATION,
        ) ||
        nextLocation.pathname.includes(
          OrderRoutePathsEnum.ORDER_PROLONG_CONFIRMATION,
        ) ||
        nextLocation.pathname.includes(RentalsRoutePathsEnum.RENTALS) ||
        mutationError

      if (isNavigationEnabled) {
        setIsNavigationAllowed(true)

        return false
      }

      return !isNavigationAllowed
    },
  )

  // Read handlers through a ref so they stay out of the guard effect deps:
  // hideModal changes identity on every modal state change, so depending on it
  // while the effect calls setModal would spin an infinite render loop.
  const handlersRef = useRef({ handleLeaveOrder, hideModal, setModal })

  useEffect(() => {
    handlersRef.current = { handleLeaveOrder, hideModal, setModal }
  })

  // Drive the modal from the current-render blocker so proceed()/reset() run
  // against the live one. Cleanup closes the modal on unmount, so an orphaned
  // modal can't resolve a deleted blocker key (Sentry: "Invalid blocker state
  // transition: unblocked -> proceeding").
  useEffect(() => {
    if (blocker.state !== 'blocked') {
      return undefined
    }

    // Resolve the blocker at most once; a second proceed()/reset() throws.
    let isResolved = false

    handlersRef.current.setModal({
      component: OrderGuardModalContent,
      contentClassName: 'OrderGuardModal-content',
      isOpen: true,
      props: {
        onContinue: () => {
          if (!isResolved) {
            isResolved = true
            blocker.reset?.()
          }

          handlersRef.current.hideModal()
        },
        onLeave: () => {
          if (!isResolved) {
            isResolved = true
            handlersRef.current.handleLeaveOrder()
            blocker.proceed?.()
          }

          handlersRef.current.hideModal()
        },
      },
    })

    return () => {
      handlersRef.current.hideModal()
    }
  }, [blocker])

  useEffect(() => {
    const beforeunloadHandler = (event: BeforeUnloadEvent) => {
      if (isNavigationAllowed) {
        return null
      }

      const e = event || window.event

      e.preventDefault()
      event.returnValue = ''

      return ''
    }

    window.addEventListener('beforeunload', beforeunloadHandler)

    return () => {
      window.removeEventListener('beforeunload', beforeunloadHandler)
    }
  }, [isNavigationAllowed])

  return null
})
